/**
 * Working-OP form status for the Planning-P rail.
 *
 * Signature status reuses the card-side completion helpers. Unsigned forms also
 * split into empty / incomplete draft / complete draft so the 202 can show
 * "every authored field is filled, not yet signed."
 */

import { buildFormCompletion } from '../femaCards/formSignatureCompletion';
import {
  authoredSections,
  getFormSectionSchema,
  incidentInfoSources,
} from '../femaCards/formSectionSchema';
import type { IcsFormFamily } from '../formFamily';
import type { IapAttachmentManifest } from '../iapAttachmentManifest';
import type { IcsFormSignatureSlot } from '../hooks/icsFormVersionHooks';
import type { WalaSignature } from './widgets/AssignmentList/ics204SignatureSlots';
import { getSignatureCompletion } from './widgets/AssignmentList/ics204SignatureSlots';
import { getIcs215SignatureCompletion } from './widgets/Ics215Table/ics215SignatureSlots';

export type StepperFormStatus =
  | 'empty'
  | 'incomplete-draft'
  | 'complete-draft'
  | 'partially-signed'
  | 'signed';

export type StepperFormKind = 'single' | 'multi';

export type StepperFormWidgetConfig = {
  baseId: string;
  kind: StepperFormKind;
};

/**
 * Widgets that have per-OP version / signature status.
 *
 * The Planning P rail reads the ones it renders; Approved ICS Forms reads the whole map to decide which forms a past
 * period actually holds. The 201 is here for the latter — it is a versioned form, just not a rail step.
 */
export const STEPPER_FORM_WIDGETS: Record<string, StepperFormWidgetConfig> = {
  ics201: { baseId: 'ICS_201', kind: 'single' },
  incidentObjectives: { baseId: 'ICS_202', kind: 'single' },
  organizationAssignmentList: { baseId: 'ICS_203', kind: 'single' },
  assignmentList: { baseId: 'ICS_204', kind: 'multi' },
  incidentRadioCommunicationsPlan: { baseId: 'ICS_205', kind: 'single' },
  communicationsList205a: { baseId: 'ICS_205A', kind: 'single' },
  medicalPlan: { baseId: 'ICS_206', kind: 'single' },
  safetyPlan: { baseId: 'ICS_208', kind: 'single' },
  hazardousMaterials: { baseId: 'ICS_208HM', kind: 'single' },
  incidentStatusSummary: { baseId: 'ICS_209', kind: 'single' },
  operationalPlanningWorksheet: { baseId: 'ICS_215', kind: 'single' },
  iapSafetyAnalysis: { baseId: 'ICS_215A', kind: 'single' },
  workAnalysisMatrix: { baseId: 'ICS_234', kind: 'single' },
  resourceRequestForm: { baseId: 'ICS_213RR', kind: 'multi' },
};

export type StepperFormStatusInfo = {
  kind: StepperFormKind;
  status: StepperFormStatus;
  signedCount?: number;
  total?: number;
  draftCount?: number;
  partiallySignedCount?: number;
  fieldComplete?: boolean;
};

export type StepperFormTip = {
  formBaseId: string;
  signatures?: WalaSignature[] | null;
  signatureSlots?: IcsFormSignatureSlot[] | null;
};

export type Stepper204Instance = {
  operationalPeriodNumber: number;
  archived?: boolean;
  signatures?: WalaSignature[] | null;
};

export type Stepper213RrInstance = {
  archived?: boolean;
  status?: string;
  section_chief_signature?: string | null;
  resl_signature?: string | null;
  logistics_signature?: string | null;
  finance_signature?: string | null;
  final_signature?: string | null;
};

export type Stepper215Tip = {
  signatures?: WalaSignature[] | null;
} | null;

const ICS_202_OMIT = new Set(['otherAttachmentsText']);
const ICS_202_USCG_OMIT = new Set([
  'otherAttachmentsText',
  'attached',
  'situationalAwareness',
  'siteSafetyPlan',
  'siteSafetyPlanLocation',
]);

const isNonEmptyText = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const isAttachmentBoxFilled = (
  stored: IapAttachmentManifest | null | undefined
): boolean => {
  if (!stored) return false;
  return (
    stored.forms.length > 0 ||
    stored.mapChart === true ||
    stored.weatherTides === true ||
    stored.otherAttachments.length > 0
  );
};

export type Ics202CompletenessInput = {
  incidentInfo?: Record<string, unknown> | null;
  hasObjectives: boolean;
  attachmentManifest?: IapAttachmentManifest | null;
  family?: IcsFormFamily;
};

export type Ics202FieldCheck = {
  source: string;
  filled: boolean;
};

/** Authored 202 boxes, excluding auto/signature/omit. */
export function ics202FieldChecks(
  input: Ics202CompletenessInput
): Ics202FieldCheck[] {
  const schema = getFormSectionSchema('ICS_202');
  if (!schema) return [];

  const omit = input.family === 'uscg' ? ICS_202_USCG_OMIT : ICS_202_OMIT;
  const info = input.incidentInfo ?? {};
  const fieldMap = incidentInfoSources('ICS_202');
  const siteSafetyPlan = info[fieldMap.siteSafetyPlan ?? 'siteSafetyPlan'];

  return authoredSections(schema)
    .filter((section) => !omit.has(section.source))
    .map((section) => {
      const source = section.source;
      if (source === 'objectives') {
        return { source, filled: input.hasObjectives };
      }
      if (source === 'attached') {
        return {
          source,
          filled: isAttachmentBoxFilled(input.attachmentManifest),
        };
      }
      if (source === 'siteSafetyPlan') {
        return {
          source,
          filled: siteSafetyPlan === true || siteSafetyPlan === false,
        };
      }
      if (source === 'siteSafetyPlanLocation') {
        if (siteSafetyPlan === true) {
          return {
            source,
            filled: isNonEmptyText(
              info[fieldMap.siteSafetyPlanLocation ?? 'siteSafetyPlanLocation']
            ),
          };
        }
        if (siteSafetyPlan === false) {
          return { source, filled: true };
        }
        return { source, filled: false };
      }
      const column = fieldMap[source] ?? source;
      return { source, filled: isNonEmptyText(info[column]) };
    });
}

export function ics202Completeness(input: Ics202CompletenessInput): {
  hasAnyContent: boolean;
  fieldComplete: boolean;
} {
  const checks = ics202FieldChecks(input);
  return {
    hasAnyContent: checks.some((check) => check.filled),
    fieldComplete: checks.length > 0 && checks.every((check) => check.filled),
  };
}

function statusFromSignatureAndFields({
  signatureStatus,
  hasTip,
  hasAnyContent,
  fieldComplete,
}: {
  signatureStatus: 'draft' | 'partially-signed' | 'signed';
  hasTip: boolean;
  hasAnyContent: boolean;
  fieldComplete: boolean;
}): StepperFormStatus {
  if (signatureStatus === 'signed') return 'signed';
  if (signatureStatus === 'partially-signed') return 'partially-signed';
  if (fieldComplete) return 'complete-draft';
  if (hasAnyContent || hasTip) return 'incomplete-draft';
  return 'empty';
}

export function singletonFormStatus({
  tip,
  fieldComplete = false,
  hasAnyContent = false,
}: {
  tip?: StepperFormTip | null;
  fieldComplete?: boolean;
  hasAnyContent?: boolean;
}): StepperFormStatusInfo {
  const slots = tip?.signatureSlots ?? [];
  const completion = buildFormCompletion(slots, tip?.signatures);
  const hasTip = tip != null;
  const status = statusFromSignatureAndFields({
    signatureStatus: completion.status,
    hasTip,
    hasAnyContent,
    fieldComplete,
  });
  return {
    kind: 'single',
    status,
    fieldComplete,
    signedCount: completion.status === 'signed' ? 1 : 0,
    total: 1,
  };
}

export function ics215StepperStatus(tip: Stepper215Tip): StepperFormStatusInfo {
  if (!tip) {
    return { kind: 'single', status: 'empty', fieldComplete: false, total: 1 };
  }
  const completion = getIcs215SignatureCompletion(tip.signatures);
  return {
    kind: 'single',
    status: statusFromSignatureAndFields({
      signatureStatus: completion.status,
      hasTip: true,
      hasAnyContent: true,
      fieldComplete: false,
    }),
    fieldComplete: false,
    signedCount: completion.status === 'signed' ? 1 : 0,
    total: 1,
  };
}

export function aggregate204Statuses(
  instances: Stepper204Instance[],
  workingPeriodNumber: number
): StepperFormStatusInfo {
  const periodInstances = instances.filter(
    (instance) =>
      instance.operationalPeriodNumber === workingPeriodNumber &&
      instance.archived !== true
  );
  const total = periodInstances.length;
  if (total === 0) {
    return {
      kind: 'multi',
      status: 'empty',
      signedCount: 0,
      total: 0,
      draftCount: 0,
      partiallySignedCount: 0,
    };
  }

  let signedCount = 0;
  let partiallySignedCount = 0;
  let draftCount = 0;
  for (const instance of periodInstances) {
    const status = getSignatureCompletion(instance.signatures).status;
    if (status === 'signed') signedCount += 1;
    else if (status === 'partially-signed') partiallySignedCount += 1;
    else draftCount += 1;
  }

  let status: StepperFormStatus = 'incomplete-draft';
  if (signedCount === total) status = 'signed';
  else if (partiallySignedCount > 0 || signedCount > 0) {
    status = 'partially-signed';
  }

  return {
    kind: 'multi',
    status,
    signedCount,
    total,
    draftCount,
    partiallySignedCount,
  };
}

export function aggregate213rrStatuses(
  instances: Stepper213RrInstance[]
): StepperFormStatusInfo {
  const live = instances.filter((instance) => instance.archived !== true);
  const total = live.length;
  if (total === 0) {
    return {
      kind: 'multi',
      status: 'empty',
      signedCount: 0,
      total: 0,
      draftCount: 0,
      partiallySignedCount: 0,
    };
  }

  const isSigned = (instance: Stepper213RrInstance) =>
    instance.status === 'completed' ||
    Boolean(
      instance.section_chief_signature &&
        instance.resl_signature &&
        instance.logistics_signature &&
        instance.finance_signature &&
        instance.final_signature
    );
  const isPartial = (instance: Stepper213RrInstance) =>
    Boolean(
      instance.section_chief_signature ||
        instance.resl_signature ||
        instance.logistics_signature ||
        instance.finance_signature ||
        instance.final_signature
    );

  let signedCount = 0;
  let partiallySignedCount = 0;
  let draftCount = 0;
  for (const instance of live) {
    if (isSigned(instance)) signedCount += 1;
    else if (isPartial(instance)) partiallySignedCount += 1;
    else draftCount += 1;
  }

  let status: StepperFormStatus = 'incomplete-draft';
  if (signedCount === total) status = 'signed';
  else if (partiallySignedCount > 0 || signedCount > 0) {
    status = 'partially-signed';
  }

  return {
    kind: 'multi',
    status,
    signedCount,
    total,
    draftCount,
    partiallySignedCount,
  };
}

export type BuildStepperFormStatusesInput = {
  tips: StepperFormTip[];
  tip215?: Stepper215Tip;
  walas: Stepper204Instance[];
  resourceRequests?: Stepper213RrInstance[];
  workingPeriodNumber: number;
  ics202?: Ics202CompletenessInput;
};

export function buildStepperFormStatuses(
  input: BuildStepperFormStatusesInput
): Record<string, StepperFormStatusInfo> {
  const tipByBaseId = new Map(input.tips.map((tip) => [tip.formBaseId, tip]));
  const completeness = input.ics202
    ? ics202Completeness(input.ics202)
    : { hasAnyContent: false, fieldComplete: false };

  const result: Record<string, StepperFormStatusInfo> = {};
  for (const [widgetName, config] of Object.entries(STEPPER_FORM_WIDGETS)) {
    if (config.baseId === 'ICS_204') {
      result[widgetName] = aggregate204Statuses(
        input.walas,
        input.workingPeriodNumber
      );
      continue;
    }
    if (config.baseId === 'ICS_213RR') {
      result[widgetName] = aggregate213rrStatuses(input.resourceRequests ?? []);
      continue;
    }
    if (config.baseId === 'ICS_215') {
      result[widgetName] = ics215StepperStatus(input.tip215 ?? null);
      continue;
    }
    if (config.baseId === 'ICS_202') {
      result[widgetName] = singletonFormStatus({
        tip: tipByBaseId.get('ICS_202'),
        fieldComplete: completeness.fieldComplete,
        hasAnyContent: completeness.hasAnyContent,
      });
      continue;
    }
    result[widgetName] = singletonFormStatus({
      tip: tipByBaseId.get(config.baseId),
      fieldComplete: false,
      hasAnyContent: false,
    });
  }
  return result;
}

export function formatStepperFormStatusLabel(
  railLabel: string,
  info: StepperFormStatusInfo | undefined
): string {
  if (!info || info.status === 'empty') {
    return railLabel;
  }
  if (info.kind === 'multi' && (info.total ?? 0) > 0) {
    return `${railLabel}: ${info.signedCount ?? 0} of ${info.total} signed`;
  }
  switch (info.status) {
    case 'incomplete-draft':
      return `${railLabel}: incomplete draft`;
    case 'complete-draft':
      return `${railLabel}: complete draft, not signed`;
    case 'partially-signed':
      return `${railLabel}: partially signed`;
    case 'signed':
      return `${railLabel}: signed`;
    default:
      return railLabel;
  }
}

export function formatStepperFormStatusTooltip(
  info: StepperFormStatusInfo | undefined
): string | undefined {
  if (!info || info.status === 'empty') return undefined;
  if (info.kind === 'multi' && (info.total ?? 0) > 0) {
    const parts = [
      `${info.signedCount ?? 0} signed`,
      `${info.partiallySignedCount ?? 0} partially signed`,
      `${info.draftCount ?? 0} drafts`,
    ];
    return parts.join(' · ');
  }
  switch (info.status) {
    case 'incomplete-draft':
      return 'Incomplete draft';
    case 'complete-draft':
      return 'Complete draft · not signed';
    case 'partially-signed':
      return 'Partially signed';
    case 'signed':
      return 'Signed';
    default:
      return undefined;
  }
}

export function stepperStatusDisplay(info: StepperFormStatusInfo | undefined): {
  type: 'icon' | 'count';
  status: StepperFormStatus;
  text?: string;
} {
  if (!info) return { type: 'icon', status: 'empty' };
  if (info.kind === 'multi' && (info.total ?? 0) > 0) {
    return {
      type: 'count',
      status: info.status,
      text: `${info.signedCount ?? 0}/${info.total}`,
    };
  }
  return { type: 'icon', status: info.status };
}
